<template>
  <div v-if="hasMore" ref="sentinel" class="infinite-scroll-loader">
    <div v-if="loading" class="infinite-scroll-loader__bar-track">
      <div class="infinite-scroll-loader__bar-fill"></div>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable no-undef */
import { ref, onMounted, onBeforeUnmount, watch } from 'vue'

const props = defineProps({
  // Show the spinner + label (controlled by the parent while its fetch is in flight).
  loading: { type: Boolean, default: false },
  // Keep observing / keep this element mounted. Set to false once the API says there's nothing left.
  hasMore: { type: Boolean, default: true },
  // Pass the template ref of the scrollable container (e.g. your `.feed` div) if the
  // list scrolls inside its own element rather than the whole window. Leave unset
  // (null) when the page/window itself scrolls.
  root: { type: Object, default: null },
  // How far before the sentinel enters the viewport to trigger the fetch (bigger = earlier).
  rootMargin: { type: String, default: '150px' }
})

const emit = defineEmits(['load-more'])

const sentinel = ref(null)
let observer = null

function setupObserver() {
  teardownObserver()
  if (!sentinel.value) return

  observer = new IntersectionObserver(
    (entries) => {
      if (entries[0].isIntersecting && !props.loading && props.hasMore) {
        emit('load-more')
      }
    },
    {
      root: props.root?.value ?? props.root ?? null,
      rootMargin: props.rootMargin,
      threshold: 0
    }
  )
  observer.observe(sentinel.value)
}

function teardownObserver() {
  if (observer) {
    observer.disconnect()
    observer = null
  }
}

onMounted(setupObserver)
onBeforeUnmount(teardownObserver)

// Re-attach if the sentinel gets unmounted/remounted (hasMore toggling false -> true).
watch(
  () => props.hasMore,
  (val) => {
    if (val) {
      // wait for the v-if to re-render the sentinel element first
      requestAnimationFrame(setupObserver)
    } else {
      teardownObserver()
    }
  }
)
</script>

<style scoped>
.infinite-scroll-loader {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 56px;
  width: 100%;
  padding: 16px 0;
  box-sizing: border-box;
}

.infinite-scroll-loader__bar-track {
  position: relative;
  width: 96px;
  height: 3px;
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.15);
  overflow: hidden;
}

.infinite-scroll-loader__bar-fill {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 36%;
  border-radius: 999px;
  background: linear-gradient(90deg, #22d3ee, #8b5cf6);
  animation: infinite-scroll-bar-slide 1.1s ease-in-out infinite;
}

@keyframes infinite-scroll-bar-slide {
  0% {
    left: -36%;
  }
  100% {
    left: 100%;
  }
}
</style>